// Bounded-concurrency + retry helpers. CommonJS so Jest can require directly
// (same pattern as assessment-risk.js); imported by push-modal.jsx to
// parallelize bulk care-plan/billing-note generation.
//
// Generation is the slow part of a bulk run (each patient is 1-2 sequential AI
// calls). Running them one at a time means a 50-patient month takes many
// minutes with the tab effectively frozen. generateForPatient writes only
// per-(patient,month) keys — no shared mutable state — so a small concurrency
// pool is safe. (The PUSH path is NOT parallelized here: it does a
// read-modify-write of the shared billed-state blob, which would race.)

// Run `worker(item, index)` over `items` with at most `limit` in flight.
// Results are returned in input order. The worker is responsible for its own
// error handling (so one failure doesn't abort the batch); if it throws, that
// rejection propagates.
async function mapWithConcurrency(items, limit, worker) {
  const arr = Array.from(items || []);
  const results = new Array(arr.length);
  if (!arr.length) return results;
  const lim = Math.max(1, Math.min(limit || 1, arr.length));
  let next = 0;
  async function runner() {
    while (true) {
      const i = next++;
      if (i >= arr.length) return;
      // eslint-disable-next-line no-await-in-loop
      results[i] = await worker(arr[i], i);
    }
  }
  const runners = [];
  for (let k = 0; k < lim; k++) runners.push(runner());
  await Promise.all(runners);
  return results;
}

// Retry an async `fn` with exponential backoff. Retries on a thrown error
// (gated by retryOnError) AND on a returned value the caller flags as transient
// (retryOnResult) — generateForPatient returns { ok:false } for transient AI
// failures rather than throwing, so both paths matter. On a retryable result
// that never recovers, the last result is returned (not thrown).
async function retryAsync(fn, opts) {
  const o = opts || {};
  const attempts = o.attempts != null ? o.attempts : 2;
  const baseMs = o.baseMs != null ? o.baseMs : 400;
  const retryOnError = o.retryOnError || (() => true);
  const retryOnResult = o.retryOnResult || (() => false);

  let lastErr;
  let lastResult;
  let haveResult = false;
  for (let i = 0; i < attempts; i++) {
    const isLast = i === attempts - 1;
    try {
      lastResult = await fn();
      haveResult = true;
      if (!isLast && retryOnResult(lastResult)) {
        await new Promise((r) => setTimeout(r, baseMs * Math.pow(2, i)));
        continue;
      }
      return lastResult;
    } catch (e) {
      lastErr = e;
      haveResult = false;
      if (!isLast && retryOnError(e)) {
        await new Promise((r) => setTimeout(r, baseMs * Math.pow(2, i)));
        continue;
      }
      throw e;
    }
  }
  if (haveResult) return lastResult;
  throw lastErr;
}

module.exports = { mapWithConcurrency, retryAsync };
